import { useLocalStorageState } from "@istari-frontend/shared";

import { EXPERIMENTAL_FEATURES } from "./registry";
import type { ExperimentalFeatureId } from "./registry";
import {
  readExperimentalFeatureEnv,
  resolveExperimentalFeature,
  warnAboutMisconfiguredExperimentalIds,
} from "./resolve-experimental-feature";
import type { ExperimentalFeatureState } from "./resolve-experimental-feature";

export type ExperimentalFeatureToggle = ExperimentalFeatureState & {
  // False while a feature this one requires is off; the switch then can't be turned on.
  requirementMet: boolean;
  setEnabled: (next: boolean) => void;
};

export function useExperimentalFeatureToggle(id: ExperimentalFeatureId): ExperimentalFeatureToggle {
  const feature = EXPERIMENTAL_FEATURES[id];
  // Tri-state: an absent key means "no user choice yet", so the environment default applies.
  const [storedChoice, setStoredChoice] = useLocalStorageState<boolean | null>(feature.storageKey, null);
  // A feature without a requirement reads its own key here, so every call runs the same hooks.
  const required = feature.requires ? EXPERIMENTAL_FEATURES[feature.requires] : feature;
  const [requiredChoice] = useLocalStorageState<boolean | null>(required.storageKey, null);
  const env = readExperimentalFeatureEnv();

  warnAboutMisconfiguredExperimentalIds(env);

  const own = resolveExperimentalFeature(feature, env, storedChoice);
  const requirementMet = !feature.requires || resolveExperimentalFeature(required, env, requiredChoice).enabled;
  const state: ExperimentalFeatureState = own.available ? { ...own, enabled: own.enabled && requirementMet } : own;
  return { ...state, requirementMet, setEnabled: setStoredChoice };
}

export function useExperimentalFeature(id: ExperimentalFeatureId): ExperimentalFeatureState {
  const { setEnabled, requirementMet, ...state } = useExperimentalFeatureToggle(id);

  return state;
}
